// components/CalculoPreciosModal.js
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Modal,
  Form,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Space,
  Table,
  Typography,
  message,
  Row,
  Col,
  Statistic,
  Divider,
  Tag,
  Tooltip,
  Descriptions,
  Alert,
  Tabs,
  Card,
  Spin,
  Collapse,
  Badge
} from 'antd';
import {
  CalculatorOutlined,
  DeleteOutlined,
  PlusOutlined,
  CalendarOutlined,
  TeamOutlined,
  EuroOutlined,
  FileTextOutlined,
  HistoryOutlined,
  PrinterOutlined,
  DownloadOutlined,
  HomeOutlined,
  CoffeeOutlined,
  ShoppingOutlined,
  CarryOutOutlined,
  CloseOutlined,
  UserOutlined,
  SmileOutlined,
  RobotOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/es_ES';

import { BASE_URL } from '../utils/constants';

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Panel } = Collapse;

// Mapa de iconos y colores por régimen
const regimenConfig = {
  HA: { icon: <HomeOutlined />, color: 'blue', desc: 'Alojamiento' },
  HD: { icon: <CoffeeOutlined />, color: 'green', desc: 'Alojamiento y Desayuno' },
  MP: { icon: <ShoppingOutlined />, color: 'orange', desc: 'Media Pensión' },
  PC: { icon: <CarryOutOutlined />, color: 'purple', desc: 'Pensión Completa' },
  TI: { icon: <CalculatorOutlined />, color: 'red', desc: 'Todo Incluido' }
};

const CalculoPreciosModal = forwardRef(({
  visible,
  onClose,
  onSuccess,
  contratoInicial = null,
  fechasIniciales = null,
  habitacionesIniciales = null,
  titulo = "Calculadora de Precios",
  width = 1400,
  allowPrint = true,
  allowExport = true,
  showHistorial = true
}, ref) => {
  // Estados
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [calculando, setCalculando] = useState(false);
  const [contratos, setContratos] = useState([]);
  const [regimenes, setRegimenes] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [ultimoContrato, setUltimoContrato] = useState(null);
  const [habitaciones, setHabitaciones] = useState([
    { 
      id: Date.now(), 
      regimen: 'HA', 
      adultos: 2, 
      ninios: 0, 
      bebes: 0,
      edades: { ninios: [] }
    }
  ]);
  const [modalVisible, setModalVisible] = useState(false);
  const [expandirDetalle, setExpandirDetalle] = useState({});

  // Exponer métodos al componente padre
  useImperativeHandle(ref, () => ({
    openModal: (config = {}) => {
      if (config.contrato) {
        form.setFieldsValue({ contrato: config.contrato });
      }
      
      if (config.fechas && config.fechas.length === 2) {
        form.setFieldsValue({ fechas: config.fechas });
      }
      
      if (config.habitaciones && config.habitaciones.length > 0) {
        setHabitaciones(config.habitaciones.map((h, index) => ({
          id: Date.now() + index,
          ...h,
          edades: h.edades || { ninios: [] }
        })));
      }
      
      setModalVisible(true);
    },
    closeModal: () => {
      handleClose();
    },
    setResultado: (data) => {
      setResultado(data);
    }
  }));

  // Sincronizar con prop visible
  useEffect(() => {
    setModalVisible(visible);
  }, [visible]);

  // Cargar datos al abrir
  useEffect(() => {
    if (modalVisible) {
      fetchContratos();
      fetchRegimenes();
      fetchUltimoContrato();
    }
  }, [modalVisible]);

  // Establecer valores iniciales
  useEffect(() => {
    if (contratoInicial) {
      form.setFieldsValue({ contrato: contratoInicial });
    }
    if (fechasIniciales) {
      form.setFieldsValue({ fechas: fechasIniciales });
    }
    if (habitacionesIniciales) {
      setHabitaciones(habitacionesIniciales.map((h, index) => ({
        id: Date.now() + index,
        ...h,
        edades: h.edades || { ninios: [] }
      })));
    }
  }, [contratoInicial, fechasIniciales, habitacionesIniciales]);

  const fetchContratos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/contratos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContratos(response.data.data || []);
    } catch (error) {
      console.error('Error cargando contratos:', error);
      message.error('Error al cargar los contratos');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegimenes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/tiposregimen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegimenes(response.data.data || []);
    } catch (error) {
      console.error('Error cargando regímenes:', error);
    }
  };

  const fetchUltimoContrato = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/calculo-precios/ultimo-contrato`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.data && !form.getFieldValue('contrato')) {
        setUltimoContrato(response.data.data);
        form.setFieldsValue({ contrato: response.data.data });
      }
    } catch (error) {
      console.error('Error obteniendo último contrato:', error);
    }
  };

  const guardarUltimoContrato = async (contratoCodigo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BASE_URL}/api/calculo-precios/ultimo-contrato`, 
        { contratoCodigo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error guardando último contrato:', error);
    }
  };

  const agregarHabitacion = () => {
    const nuevaHabitacion = {
      id: Date.now(),
      regimen: 'HA',
      adultos: 2,
      ninios: 0,
      bebes: 0,
      edades: { ninios: [] }
    };
    setHabitaciones([...habitaciones, nuevaHabitacion]);
  };

  const eliminarHabitacion = (id) => {
    if (habitaciones.length === 1) {
      message.warning('Debe haber al menos una habitación');
      return;
    }
    setHabitaciones(habitaciones.filter(h => h.id !== id));
  };

  const actualizarHabitacion = (id, campo, valor) => {
    setHabitaciones(habitaciones.map(h => {
      if (h.id === id) {
        const nuevaHabitacion = { ...h, [campo]: valor };
        
        // Si cambia número de niños, ajustar array de edades
        if (campo === 'ninios') {
          const nuevasEdades = [];
          for (let i = 0; i < valor; i++) {
            nuevasEdades.push(h.edades?.ninios?.[i] || null);
          }
          nuevaHabitacion.edades = { ninios: nuevasEdades };
        }
        
        return nuevaHabitacion;
      }
      return h;
    }));
  };

  const actualizarEdadNino = (idHabitacion, indexNino, edad) => {
    setHabitaciones(habitaciones.map(h => {
      if (h.id === idHabitacion) {
        const nuevasEdades = [...(h.edades?.ninios || [])];
        nuevasEdades[indexNino] = edad;
        return {
          ...h,
          edades: { ninios: nuevasEdades }
        };
      }
      return h;
    }));
  };

  const handleCalcular = async (values) => {
    setCalculando(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (!values.fechas || values.fechas.length !== 2) {
        message.error('Por favor seleccione fechas de entrada y salida');
        return;
      }

      const fechaEntrada = values.fechas[0].format('YYYY-MM-DD');
      const fechaSalida = values.fechas[1].format('YYYY-MM-DD');

      const habitacionesData = habitaciones.map(h => ({
        regimen: h.regimen,
        adultos: h.adultos,
        ninios: h.ninios,
        bebes: h.bebes,
        edades: h.edades
      }));

      const payload = {
        contratoCodigo: values.contrato,
        fechaEntrada,
        fechaSalida,
        habitaciones: habitacionesData
      };

      const response = await axios.post(
        `${BASE_URL}/api/calculo-precios/calcular`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResultado(response.data.data);
      await guardarUltimoContrato(values.contrato);
      
      if (onSuccess) {
        onSuccess(response.data.data);
      }
      
      message.success('Cálculo realizado correctamente');

    } catch (error) {
      console.error('Error en cálculo:', error);
      message.error(error.response?.data?.error || 'Error al realizar el cálculo');
    } finally {
      setCalculando(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setHabitaciones([{ 
      id: Date.now(), 
      regimen: 'HA', 
      adultos: 2, 
      ninios: 0, 
      bebes: 0,
      edades: { ninios: [] }
    }]);
    setResultado(null);
  };

  const handleClose = () => {
    setModalVisible(false);
    handleReset();
    if (onClose) onClose();
  };

  const handlePrint = () => {
    const printContent = document.getElementById('resultado-print');
    if (printContent) {
      const originalTitle = document.title;
      document.title = `Calculo_Precios_${dayjs().format('YYYYMMDD_HHmmss')}`;
      window.print();
      document.title = originalTitle;
    }
  };

  const handleExportCSV = () => {
    if (!resultado) return;

    let csv = 'Habitación,Fecha,Régimen,Tipo Persona,Nº,Edad,Precio\n';
    
    resultado.habitaciones.forEach(hab => {
      hab.preciosDiarios.forEach(dia => {
        // Adultos
        dia.detalleAdultos.forEach(adulto => {
          csv += `${hab.habitacion},${dia.fechaFormateada},${hab.regimen},Adulto,${adulto.adulto},,${adulto.precio}\n`;
        });
        // Niños
        dia.detalleNinios.forEach(nino => {
          csv += `${hab.habitacion},${dia.fechaFormateada},${hab.regimen},Niño,${nino.nino},${nino.edad},${nino.precio}\n`;
        });
        // Bebés
        dia.detalleBebes.forEach(bebe => {
          csv += `${hab.habitacion},${dia.fechaFormateada},${hab.regimen},Bebé,${bebe.bebe},,${bebe.precio}\n`;
        });
      });
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `calculo_precios_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleDetalle = (habitacionId, fecha) => {
    const key = `${habitacionId}-${fecha}`;
    setExpandirDetalle(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Columnas para tablas
  const columnasResumen = [
    {
      title: 'Hab.',
      dataIndex: 'habitacion',
      key: 'habitacion',
      width: 80,
      render: (num) => <Tag color="blue">#{num}</Tag>
    },
    {
      title: 'Régimen',
      dataIndex: 'regimen',
      key: 'regimen',
      width: 150,
      render: (reg) => {
        const config = regimenConfig[reg] || { icon: <FileTextOutlined />, color: 'default' };
        return (
          <Tag color={config.color} icon={config.icon}>
            {reg} - {config.desc}
          </Tag>
        );
      }
    },
    {
      title: 'Ocupación',
      dataIndex: 'totalPersonas',
      key: 'totalPersonas',
      render: (_, record) => (
        <div>
          <div><UserOutlined /> {record.adultos} Adultos</div>
          {record.ninios > 0 && <div><SmileOutlined /> {record.ninios} Niños</div>}
          {record.bebes > 0 && <div><RobotOutlined /> {record.bebes} Bebés</div>}
        </div>
      )
    },
    {
      title: 'Total',
      dataIndex: 'totalHabitacion',
      key: 'totalHabitacion',
      align: 'right',
      render: (total) => (
        <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
          {total?.toFixed(2)} €
        </Text>
      )
    }
  ];

  const columnasDiario = [
    {
      title: 'Fecha',
      dataIndex: 'fechaFormateada',
      key: 'fecha',
      width: 100
    },
    {
      title: 'Precios Base',
      key: 'preciosBase',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue">PC: {record.precioBase.PC}€</Tag>
          <Tag color="green">MP: {record.precioBase.MP}€</Tag>
          <Tag color="orange">HD: {record.precioBase.HD}€</Tag>
          <Tag color="purple">HA: {record.precioBase.HA}€</Tag>
          <Tag color="red">TI: {record.precioBase.TI}€</Tag>
        </Space>
      )
    },
    {
      title: 'Detalle',
      key: 'detalle',
      width: 300,
      render: (_, record) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {record.detalleAdultos.map(a => (
            <Badge 
              key={`a-${a.adulto}`}
              status="success" 
              text={`Adulto ${a.adulto}: ${a.precio}€`} 
            />
          ))}
          {record.detalleNinios.map(n => (
            <Badge 
              key={`n-${n.nino}`}
              status="warning" 
              text={`Niño ${n.nino} (${n.edad} años): ${n.precio}€`} 
            />
          ))}
          {record.detalleBebes.map(b => (
            <Badge 
              key={`b-${b.bebe}`}
              status="default" 
              text={`Bebé ${b.bebe}: ${b.precio}€`} 
            />
          ))}
        </Space>
      )
    },
    {
      title: 'Total Noche',
      dataIndex: 'precioCalculado',
      key: 'precioCalculado',
      align: 'right',
      width: 120,
      render: (precio) => (
        <Text strong style={{ color: '#52c41a', fontSize: '15px' }}>
          {precio?.toFixed(2)} €
        </Text>
      )
    }
  ];

  return (
    <Modal
      title={
        <Space>
          <CalculatorOutlined style={{ color: '#1890ff' }} />
          <span>{titulo}</span>
        </Space>
      }
      open={modalVisible}
      onCancel={handleClose}
      width={width}
      footer={null}
      closeIcon={<CloseOutlined />}
      style={{ top: 20 }}
      bodyStyle={{ 
        maxHeight: 'calc(100vh - 100px)', 
        overflowY: 'auto',
        padding: '16px'
      }}
    >
      <Spin spinning={loading || calculando}>
        <Row gutter={[16, 16]}>
          {/* Columna izquierda - Formulario */}
          <Col xs={24} lg={8}>
            <Card 
              size="small"
              title={
                <Space>
                  <CalculatorOutlined />
                  <span>Datos de la estancia</span>
                </Space>
              }
              style={{ borderRadius: '8px' }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleCalcular}
              >
                <Form.Item
                  name="contrato"
                  label="Contrato"
                  rules={[{ required: true, message: 'Seleccione un contrato' }]}
                >
                  <Select
                    showSearch
                    placeholder="Buscar contrato..."
                    optionFilterProp="children"
                    size="middle"
                  >
                    {contratos.map(contrato => (
                      <Option key={contrato.Codigo} value={contrato.Codigo}>
                        {contrato.Codigo} - {contrato.Descripcion}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="fechas"
                  label="Fechas"
                  rules={[{ required: true, message: 'Seleccione las fechas' }]}
                >
                  <RangePicker
                    locale={locale}
                    format="DD/MM/YYYY"
                    style={{ width: '100%' }}
                    size="middle"
                    disabledDate={(current) => current && current < dayjs().startOf('day')}
                  />
                </Form.Item>

                <Divider style={{ margin: '12px 0' }}>Habitaciones</Divider>

                {habitaciones.map((habitacion) => (
                  <Card
                    key={habitacion.id}
                    size="small"
                    title={`Habitación ${habitacion.id.toString().slice(-4)}`}
                    extra={
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => eliminarHabitacion(habitacion.id)}
                        size="small"
                      />
                    }
                    style={{ marginBottom: 12, backgroundColor: '#f9f9f9' }}
                  >
                    <Form.Item label="Régimen" style={{ marginBottom: 8 }}>
                      <Select
                        value={habitacion.regimen}
                        onChange={(val) => actualizarHabitacion(habitacion.id, 'regimen', val)}
                        size="small"
                      >
                        {regimenes.map(reg => (
                          <Option key={reg.Codigo} value={reg.Codigo}>
                            {reg.Codigo} - {reg.DescripcionCorta || reg.Descripcion}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Row gutter={4}>
                      <Col span={8}>
                        <Form.Item label="Adultos" style={{ marginBottom: 4 }}>
                          <InputNumber
                            min={1}
                            max={4}
                            value={habitacion.adultos}
                            onChange={(val) => actualizarHabitacion(habitacion.id, 'adultos', val)}
                            size="small"
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label="Niños" style={{ marginBottom: 4 }}>
                          <InputNumber
                            min={0}
                            max={3}
                            value={habitacion.ninios}
                            onChange={(val) => actualizarHabitacion(habitacion.id, 'ninios', val)}
                            size="small"
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label="Bebés" style={{ marginBottom: 4 }}>
                          <InputNumber
                            min={0}
                            max={2}
                            value={habitacion.bebes}
                            onChange={(val) => actualizarHabitacion(habitacion.id, 'bebes', val)}
                            size="small"
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Edades de niños */}
                    {habitacion.ninios > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>Edades de los niños:</Text>
                        <Row gutter={4} style={{ marginTop: 4 }}>
                          {[...Array(habitacion.ninios)].map((_, idx) => (
                            <Col span={8} key={idx}>
                              <InputNumber
                                placeholder={`Niño ${idx + 1}`}
                                min={0}
                                max={17}
                                value={habitacion.edades?.ninios?.[idx]}
                                onChange={(val) => actualizarEdadNino(habitacion.id, idx, val)}
                                size="small"
                                style={{ width: '100%' }}
                              />
                            </Col>
                          ))}
                        </Row>
                        <Text type="secondary" style={{ fontSize: '10px' }}>
                          <InfoCircleOutlined /> Dejar vacío = primer rango
                        </Text>
                      </div>
                    )}
                  </Card>
                ))}

                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={agregarHabitacion}
                  block
                  size="small"
                  style={{ marginBottom: 16 }}
                >
                  Añadir habitación
                </Button>

                <Space style={{ width: '100%' }} direction="vertical">
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<CalculatorOutlined />}
                    block
                    loading={calculando}
                  >
                    Calcular
                  </Button>
                  
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={handleReset}
                    block
                    size="small"
                  >
                    Limpiar
                  </Button>
                </Space>

                {showHistorial && ultimoContrato && (
                  <Alert
                    style={{ marginTop: 12 }}
                    message={`Último contrato: ${ultimoContrato}`}
                    type="info"
                    showIcon
                    icon={<HistoryOutlined />}
                  />
                )}
              </Form>
            </Card>
          </Col>

          {/* Columna derecha - Resultados */}
          <Col xs={24} lg={16}>
            {resultado ? (
              <div id="resultado-print">
                <Card
                  size="small"
                  title={
                    <Space>
                      <FileTextOutlined />
                      <span>Resultado del Cálculo</span>
                    </Space>
                  }
                  extra={
                    <Space size={4}>
                      {allowPrint && (
                        <Tooltip title="Imprimir">
                          <Button 
                            icon={<PrinterOutlined />} 
                            onClick={handlePrint}
                            size="small"
                          />
                        </Tooltip>
                      )}
                      {allowExport && (
                        <Tooltip title="Exportar CSV">
                          <Button 
                            icon={<DownloadOutlined />} 
                            onClick={handleExportCSV}
                            size="small"
                          />
                        </Tooltip>
                      )}
                    </Space>
                  }
                >
                  {/* Resumen rápido */}
                  <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
                    <Col span={24}>
                      <Tag color="blue" style={{ width: '100%', padding: '4px', textAlign: 'center' }}>
                        <FileTextOutlined /> {resultado.contrato.codigo} - {resultado.contrato.descripcion}
                      </Tag>
                    </Col>
                    
                    <Col xs={24} sm={8}>
                      <Statistic
                        title="Total Estancia"
                        value={resultado.totales.totalGeneral}
                        precision={2}
                        suffix={resultado.contrato.moneda}
                        prefix={<EuroOutlined />}
                        valueStyle={{ color: '#1890ff', fontSize: '20px' }}
                      />
                    </Col>
                    
                    <Col xs={24} sm={8}>
                      <Statistic
                        title="Noches"
                        value={resultado.estancia.noches}
                        suffix="noches"
                        prefix={<CalendarOutlined />}
                        valueStyle={{ fontSize: '20px' }}
                      />
                    </Col>
                    
                    <Col xs={24} sm={8}>
                      <Statistic
                        title="Personas"
                        value={resultado.totales.totalPersonas}
                        suffix={` (${resultado.totales.totalAdultos} A, ${resultado.totales.totalNinios} N, ${resultado.totales.totalBebes} B)`}
                        prefix={<TeamOutlined />}
                        valueStyle={{ fontSize: '16px' }}
                      />
                    </Col>
                  </Row>

                  <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Entrada">
                      {resultado.estancia.fechaEntradaFormateada}
                    </Descriptions.Item>
                    <Descriptions.Item label="Salida">
                      {resultado.estancia.fechaSalidaFormateada}
                    </Descriptions.Item>
                  </Descriptions>

                  {/* Resumen por habitaciones */}
                  <Table
                    columns={columnasResumen}
                    dataSource={resultado.habitaciones}
                    pagination={false}
                    rowKey="habitacion"
                    size="small"
                    style={{ marginBottom: 16 }}
                  />

                  {/* Detalle diario por habitación */}
                  <Tabs defaultActiveKey="1" size="small" type="card">
                    {resultado.habitaciones.map((hab) => (
                      <TabPane 
                        tab={
                          <span>
                            <HomeOutlined />
                            Hab {hab.habitacion}
                          </span>
                        } 
                        key={hab.habitacion.toString()}
                      >
                        <Table
                          columns={columnasDiario}
                          dataSource={hab.preciosDiarios}
                          pagination={false}
                          rowKey="fecha"
                          size="small"
                          scroll={{ x: 800 }}
                          summary={() => (
                            <Table.Summary fixed>
                              <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={3}>
                                  <Text strong>Total Habitación {hab.habitacion}</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                  <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                                    {hab.totalHabitacion.toFixed(2)} €
                                  </Text>
                                </Table.Summary.Cell>
                              </Table.Summary.Row>
                            </Table.Summary>
                          )}
                        />
                      </TabPane>
                    ))}
                  </Tabs>

                  {/* Desglose por régimen */}
                  <Collapse 
                    size="small" 
                    style={{ marginTop: 16, backgroundColor: '#fafafa' }}
                  >
                    <Panel header="Desglose por régimen" key="1">
                      <Row gutter={[16, 16]}>
                        {Object.entries(resultado.totales.totalPorRegimen).map(([reg, total]) => (
                          total > 0 && (
                            <Col xs={24} sm={12} md={8} key={reg}>
                              <Card size="small">
                                <Statistic
                                  title={
                                    <Space>
                                      {regimenConfig[reg]?.icon}
                                      <span>{reg} - {regimenConfig[reg]?.desc}</span>
                                    </Space>
                                  }
                                  value={total}
                                  precision={2}
                                  suffix="€"
                                  valueStyle={{ color: regimenConfig[reg]?.color }}
                                />
                              </Card>
                            </Col>
                          )
                        ))}
                      </Row>
                    </Panel>
                  </Collapse>
                </Card>
              </div>
            ) : (
              <Card
                style={{ 
                  height: '100%', 
                  minHeight: 500,
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: '#fafafa'
                }}
              >
                <Space direction="vertical" align="center">
                  <CalculatorOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />
                  <Title level={4} style={{ color: '#bfbfbf', margin: 0 }}>
                    Complete los datos para calcular
                  </Title>
                  <Text type="secondary">
                    Seleccione contrato, fechas y configure las habitaciones
                  </Text>
                </Space>
              </Card>
            )}
          </Col>
        </Row>
      </Spin>

      {/* Estilos para impresión */}
      <style jsx global>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #resultado-print, #resultado-print * {
            visibility: visible;
          }
          #resultado-print {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 20px;
          }
          .ant-card-extra, .ant-tabs-nav, .ant-collapse-header, .ant-btn {
            display: none !important;
          }
          .ant-table {
            font-size: 10px;
          }
        }
      `}</style>
    </Modal>
  );
});

export default CalculoPreciosModal;